import ms from 'ms';
import { useQuery } from 'react-query';

import { Box } from '@hivebrite/design-system/Box';
import { FlagPricing, Flag } from '@hivebrite/design-system/Flag';
import { Skeleton } from '@hivebrite/design-system/Skeleton';

import type { Event, EventTicket } from 'hb-react/shared/apps/Events/types';
import { axiosWithSyntaxHandlers } from 'hb-react/shared/utils/axios';

export interface EventPricingTagProps {
  event: Pick<Event, 'id'>;
}

const EventPricingTag = ({ event }: EventPricingTagProps) => {
  const { data, isLoading } = useQuery(
    ['event', 'ticket_ranges', event.id],
    () =>
      axiosWithSyntaxHandlers
        .get<{
          data: Pick<EventTicket, 'id' | 'price' | 'currency'>[];
        }>(Routes.events_event_ticket_ranges_path(event.id))
        .then((r) => r.data?.data ?? []),
    {
      staleTime: ms('1h'),
    },
  );

  const tickets = data ?? [];

  if (isLoading) {
    return (
      <Flag
        text={
          <Box
            style={{ rowGap: '3px' }}
            flexDirection="column"
            dataTestId="loader"
          >
            <Skeleton height="5px" width="36px" />
            <Skeleton height="5px" width="32px" />
          </Box>
        }
      />
    );
  }

  if (tickets.length === 0) {
    return null;
  }

  const [min, max] = tickets;

  const currency = (min || max)?.currency;

  const isFree = Number(max?.price ?? min?.price) === 0;

  if (isFree) {
    return <FlagPricing />;
  }

  return (
    <FlagPricing
      min={min?.price}
      // by design FlagPricing component will render 0 if max is undefined eg ($0 - $99),
      // we want to avoid that and in such case we want to render only min price eg ($99
      max={max?.price ?? min?.price}
      currency={currency}
    />
  );
};

export default EventPricingTag;
